import React, { useState } from "react";

import { useGetCurrentUser } from "api/api-hooks/user-hooks";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { MdmObjectDto, MdmObjectNote, useCreateNoteMutation, useDeleteNoteMutation, useGetAllNotesQuery } from "gql/generated/types";
import { Button } from "primereact/button";
import { Card } from 'primereact/card';
import { Editor } from "primereact/editor";
import { GET_NOTES } from "gql/note.operations";

type ObjectNotesProps = {
  object: MdmObjectDto
}

const ObjectNotes: React.FC<ObjectNotesProps> = ({ object }) => {
  const [ note, setNote ] = useState<string | null>(null)
  const [createNoteMutation] = useCreateNoteMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Note created successfully",
        life: 3000,
      });
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
  });

  const {data} = useGetAllNotesQuery({
    variables: {
      id: object.id!
    },
    skip: !object.id
  })

  const handleNoteSend = () => {
    if (!note) return;
    createNoteMutation({
      variables: {
        objectId: object.id!,
        note
      },
      update: (cache, { data: qData }) => {
        cache.updateQuery({ query: GET_NOTES, variables: {id: object.id}}, (oldData) => ({ 
          allNotesByObjectId: [...oldData.allNotesByObjectId, qData?.createNote]
        }) );
      }
    })
    setNote(null)
  }

  const mapNotes = data?.allNotesByObjectId?.map(n => <ObjectNote key={n.id} note={n} />);

  return (
    <div className="mx-auto" style={{ maxWidth: "800px"}}>
      {data && data.allNotesByObjectId.length > 0 && (
        <Card style={{ maxHeight: "300px", overflowY: "auto" }}>{mapNotes}</Card>
      )}
      <Editor
        value={note ?? ""}
        onTextChange={e => setNote(e.htmlValue)}
        className="mt-3"
        style={{ minHeight: "100px" }}
      />
      <Button type="button" label="Send" onClick={handleNoteSend} className="w-auto mt-3" />
    </div>
  );
}

export default ObjectNotes;

const ObjectNote: React.FC<{note: MdmObjectNote}> = ({ note}) => {
  const {data: user} = useGetCurrentUser();
  const [deleteNoteMutation] = useDeleteNoteMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Note deleted successfully",
        life: 3000,
      });
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
  });

  const confirmOnDelete = () => {
    deleteNoteMutation({
      variables: {
        id: note.id
      },
      update(cache) {
        const normalizedId = cache.identify({ id: note.id, __typename: "MdmObjectNote" });
        cache.evict({ id: normalizedId });
        cache.gc();
      },
    })
  }

  return (
    <div id="note" className="mb-3 border-1 border-300 border-round px-3 pb-2" >
      <div className="flex justify-content-end align-items-center m-0">
        <span className="text-500 mr-auto">{user?.displayName}</span>
        <span className="text-500">{new Date(note.createdAt).toLocaleString()}</span>
        <Button
          type="button"
          icon="pi pi-trash"
          title="Delete"
          className="p-button-rounded p-button-text p-button-danger p-0"
          onClick={confirmOnDelete}
        />
      </div>
      <div dangerouslySetInnerHTML={{ __html: note.note ?? "" }} style={{ wordBreak: "break-word" }} />
    </div>
  );
}